import { App } from '../App';
import MobileDetect from 'mobile-detect';
import { IPlan } from '../interfaces/IPlan';
import PrintService from './PrintService';
import { ILanguage } from '../interfaces/ILanguage';
import { PaymentMethod } from '../enums/PaymentMethod';
import { IRestaurant } from '../interfaces/IRestaurant';
import { extractBaseHref } from '@/utils';
import { getTopLevelDomain } from '@/utils/domain.utils';
import { CURRENCIES } from '@/modules/currencies';
const md = new MobileDetect(window.navigator.userAgent);
const domain = getTopLevelDomain();

export class Globals {
  static version = '1.12.0';
  static IsLocalhost =
    typeof window === 'undefined'
      ? false
      : window.location.hostname === 'localhost' ||
        window.location.hostname === '192.168.50.50' ||
        window.location.hostname === '10.0.2.2' ||
        window.location.hostname === '127.0.0.1';
  static IsProduction =
    typeof window === 'undefined' ? false : window.location.hostname === domain;
  static basePath = Globals.IsLocalhost ? '/' : extractBaseHref('/admin/');
  static apiPath = Globals.IsLocalhost
    ? `http://${window.location.hostname}:8092`
    : location.protocol + `//api.${domain}/api`;
  static wsPath = Globals.IsLocalhost
    ? `http://${window.location.hostname}:8092`
    : `https://api.${domain}`;
  static uploadsPath = Globals.IsLocalhost
    ? `https://cdndev.${domain}`
    : `https://cdn.${domain}`;
  static website = Globals.IsLocalhost
    ? `http://${window.location.hostname}:8095`
    : `https://${domain}`;
  /** Short domain for links owners share; redirects to `website` keeping the path. */
  static shortWebsite = Globals.IsLocalhost
    ? Globals.website
    : 'https://bistri.fi';
  static adminWebsite = Globals.IsLocalhost
    ? `http://${window.location.hostname}:3000`
    : `https://${domain}/admin`;
  static isMobile =
    !!md.mobile() ||
    (navigator.userAgent.match(/Mac/) &&
      navigator.maxTouchPoints &&
      navigator.maxTouchPoints > 2);
  static isCapacitor = window['Capacitor']?.isNativePlatform?.() || false;
  /**
   * Android-only Capacitor gate: printing and the customer display are
   * Android-native features; an iOS build must behave like a mobile browser
   * for them (Tier A shell).
   */
  static isCapacitorAndroid =
    (window['Capacitor']?.isNativePlatform?.() &&
      window['Capacitor']?.getPlatform?.() === 'android') ||
    false;
  /**
   * Old installed APKs (Capacitor 5 era) keep loading this web bundle via the
   * remote-URL strategy but lack the new native plugins (EscPosPrinter,
   * CustomerDisplay, Share, Filesystem…). EscPosPrinter ships in every new
   * binary, so its absence on an Android shell identifies a legacy APK:
   * feature code must degrade gracefully and steer the user to update.
   */
  static isLegacyAndroidShell =
    Globals.isCapacitorAndroid &&
    !(window['Capacitor']?.isPluginAvailable?.('EscPosPrinter') ?? false);
  static printService: PrintService = null;
  static languages: ILanguage[] = [];
  static countries = [
    {
      value: 'AR',
      text: 'Argentina',
      lat: -38.416097,
      lon: -63.616672,
      telCode: 54,
    },
    {
      value: 'BO',
      text: 'Bolivia',
      lat: -16.290154,
      lon: -63.588653,
      telCode: 591,
    },
    {
      value: 'BR',
      text: 'Brasil',
      lat: -23.533773,
      lon: -46.62529,
      telCode: 55,
    },
    {
      value: 'UY',
      text: 'Uruguay',
      lat: -32.522779,
      lon: -55.765835,
      telCode: 598,
    },
    {
      value: 'CL',
      text: 'Chile',
      lat: -35.675147,
      lon: -71.542969,
      telCode: 56,
    },
    { value: 'ES', text: 'España', lat: 40.463667, lon: -3.74922, telCode: 34 },
    {
      value: 'MX',
      text: 'México',
      lat: 19.432608,
      lon: -99.133209,
      telCode: 52,
    },
    {
      value: 'CO',
      text: 'Colombia',
      lat: 4.570868,
      lon: -74.297333,
      telCode: 57,
    },
    {
      value: 'CR',
      text: 'Costa Rica',
      lat: 9.934739,
      lon: -84.087502,
      telCode: 506,
    },
    { value: 'PE', text: 'Peru', lat: -9.189967, lon: -75.015152, telCode: 51 },
    {
      value: 'EC',
      text: 'Ecuador',
      lat: -1.831239,
      lon: -78.183406,
      telCode: 593,
    },
    {
      value: 'PA',
      text: 'Panama',
      lat: 8.537981,
      lon: -80.782127,
      telCode: 507,
    },
    {
      value: 'US',
      text: 'Estados Unidos',
      lat: 37.09024,
      lon: -95.712891,
      telCode: 1,
    },
    {
      value: 'PY',
      text: 'Paraguay',
      lat: -25.30066,
      lon: -57.63591,
      telCode: 595,
    },
    { value: 'VE', text: 'Venezuela', lat: 10.5, lon: -66.916664, telCode: 58 },
    {
      value: 'DO',
      text: 'República Dominicana',
      lat: 18.735693,
      lon: -70.162651,
      telCode: 1809,
    },
  ];
  static currencies = CURRENCIES;
  static getCurrency(id: string) {
    return this.currencies.find((value) => value.value === id);
  }
  static socket: SocketIOClient.Socket = null;
  // Need this to access the app from Vue components
  static app: App;

  static shortPaymentMethods = [
    { id: PaymentMethod.Cash, title: 'Efectivo' },
    { id: PaymentMethod.Card, title: 'Tarjeta' },
    { id: PaymentMethod.Online, title: 'MercadoPago' },
    { id: PaymentMethod.QR, title: 'QR' },
    { id: PaymentMethod.Invitation, title: 'Invitación' },
    { id: PaymentMethod.Employee, title: 'Consumo Empleado' },
    { id: PaymentMethod.BankTransfer, title: 'Transferencia' },
    { id: PaymentMethod.CustomerAccount, title: 'Cuenta cliente' },
    { id: PaymentMethod.Club, title: 'Saldo Club' },
  ];
  static shortPaymentMethosMap = Globals.shortPaymentMethods.reduce(
    (acc, val) => {
      acc[val.id] = val.title;
      return acc;
    },
    {}
  );

  static plans: IPlan[] = [
    {
      tier: 0,
      title: 'Free',
      color: '#4caf50',
      features: [],
    },
    {
      tier: 1,
      title: 'Plus',
      prices: [
        {
          currency: 'ARS',
          variants: {
            1: 19999,
            3: 55999,
            6: 107999,
            12: 199999,
          },
        },
        {
          currency: 'USD',
          variants: {
            1: 9.99,
            3: 27.99,
            6: 53.99,
            12: 99.99,
          },
        },
      ],
      color: 'orange',
      features: [],
    },
    {
      tier: 2,
      title: 'Pro',
      prices: [
        {
          currency: 'ARS',
          variants: {
            1: 34999,
            3: 97999,
            6: 188999,
            12: 349999,
          },
        },
        {
          currency: 'USD',
          variants: {
            1: 24.99,
            3: 69.99,
            6: 134.99,
            12: 249.99,
          },
        },
      ],
      color: '#ff6f00',
      features: [],
    },
  ];
  static paymentMethods = [
    {
      id: 'mp',
      type: 'stored',
      name: 'Tarjeta de crédito / débito',
      currencies: ['ARS'],
    },
    {
      id: 'stripe',
      type: 'stored',
      name: 'Tarjeta de crédito / débito',
      currencies: ['USD'],
    },
    {
      id: 'mp',
      name: 'MercadoPago',
      currencies: ['ARS'],
    },
    {
      id: 'paypal',
      name: 'PayPal',
      currencies: ['USD'],
    },
  ];

  static days = [
    { val: 1, name: 'monday' },
    { val: 2, name: 'tuesday' },
    { val: 3, name: 'wednesday' },
    { val: 4, name: 'thursday' },
    { val: 5, name: 'friday' },
    { val: 6, name: 'saturday' },
    { val: 0, name: 'sunday' },
    { val: 7, name: 'holidays' },
  ];

  static hasLocalStorage() {
    try {
      if (!window.localStorage) return false;
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      return true;
    } catch (e) {
      return false;
    }
  }

  static cookiesEnabled() {
    let cookieEnabled = navigator.cookieEnabled;
    if (!cookieEnabled) {
      document.cookie = 'testcookie';
      cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
    }
    return cookieEnabled;
  }

  static offlineOrderingEnabled = false;

  /** localStorage key of the store-creation draft kept before the store exists. */
  static setupDraftKey = (userId: number) => `cd-store-setup-draft:${userId}`;
}
